import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  FormInput,
  Plus,
  Loader2,
  Edit,
  Trash2,
  GripVertical,
  Type,
  Hash,
  Calendar,
  ToggleLeft,
  List,
  AlignLeft
} from "lucide-react";

export default function CamposPersonalizadosPage() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    label: "",
    type: "text",
    description: "",
    required: false,
    options: "",
    default_value: "",
    show_in_reports: true
  });
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      const res = await axios.get(`${API}/custom-fields`, getAuthHeaders());
      setFields(res.data);
    } catch (err) {
      // Default fields if endpoint doesn't exist
      setFields([
        {
          id: "1",
          name: "policy_number",
          label: "Número de Póliza",
          type: "text",
          description: "Número identificador de la póliza",
          required: true,
          show_in_reports: true
        },
        {
          id: "2",
          name: "client_name",
          label: "Nombre del Cliente",
          type: "text",
          description: "Nombre completo del cliente",
          required: true,
          show_in_reports: true
        },
        {
          id: "3",
          name: "premium_amount",
          label: "Monto de Prima",
          type: "number",
          description: "Valor de la prima en la moneda local",
          required: false,
          show_in_reports: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fieldTypes = [
    { value: "text", label: "Texto", icon: Type },
    { value: "number", label: "Número", icon: Hash },
    { value: "date", label: "Fecha", icon: Calendar },
    { value: "boolean", label: "Sí/No", icon: ToggleLeft },
    { value: "select", label: "Lista de Opciones", icon: List },
    { value: "textarea", label: "Texto Largo", icon: AlignLeft }
  ];

  const getFieldIcon = (type) => {
    const fieldType = fieldTypes.find(ft => ft.value === type);
    return fieldType ? fieldType.icon : Type;
  };

  const resetForm = () => {
    setFormData({
      name: "",
      label: "",
      type: "text",
      description: "",
      required: false,
      options: "",
      default_value: "",
      show_in_reports: true
    });
    setEditingField(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (field) => {
    setEditingField(field);
    setFormData({
      name: field.name,
      label: field.label,
      type: field.type,
      description: field.description || "",
      required: field.required || false,
      options: field.options?.join(", ") || "",
      default_value: field.default_value || "",
      show_in_reports: field.show_in_reports !== false
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.label.trim()) {
      toast.error("Nombre y etiqueta son requeridos");
      return;
    }

    // Validate name format
    const nameRegex = /^[a-z_][a-z0-9_]*$/;
    if (!nameRegex.test(formData.name)) {
      toast.error("El nombre debe ser en minúsculas, sin espacios (usa guión bajo)");
      return;
    }

    setSaving(true);
    try {
      const fieldData = {
        ...formData,
        options: formData.type === "select" ? formData.options.split(",").map(o => o.trim()).filter(o => o) : undefined
      };

      if (editingField) {
        setFields(fields.map(f => f.id === editingField.id ? { ...f, ...fieldData } : f));
        toast.success("Campo actualizado");
      } else {
        const newField = {
          id: `field_${Date.now()}`,
          ...fieldData
        };
        setFields([...fields, newField]);
        toast.success("Campo creado");
      }
      setShowDialog(false);
      resetForm();
    } catch (err) {
      toast.error("Error al guardar el campo");
    } finally {
      setSaving(false);
    }
  };

  const deleteField = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este campo?")) return;
    setFields(fields.filter(f => f.id !== id));
    toast.success("Campo eliminado");
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="campos-personalizados-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Campos Personalizados
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Crea campos adicionales para incluir en los informes
            </p>
          </div>
          <Button 
            onClick={openNewDialog}
            className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
            data-testid="new-field-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Campo
          </Button>
        </div>

        {/* Info Card */}
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 mb-6">
          <CardContent className="py-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Tip:</strong> Los campos personalizados te permiten agregar información adicional a tus informes. 
              Puedes usarlos para incluir datos específicos de cada póliza o cliente.
            </p>
          </CardContent>
        </Card>

        {/* Fields List */}
        <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
          <CardHeader className="border-b border-slate-100 dark:border-slate-700">
            <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white font-['Manrope']">
              Campos Definidos
            </CardTitle>
            <CardDescription className="dark:text-slate-400">
              {fields.length} campo(s) configurado(s)
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : fields.length === 0 ? (
              <div className="text-center py-12">
                <FormInput className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400 mb-4">No hay campos personalizados</p>
                <Button onClick={openNewDialog} variant="outline" className="border-slate-200 dark:border-slate-600 dark:text-slate-300">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear primer campo
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {fields.map((field) => {
                  const FieldIcon = getFieldIcon(field.type);
                  return (
                    <div
                      key={field.id}
                      className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                      data-testid={`field-row-${field.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-slate-300 cursor-move">
                          <GripVertical className="w-5 h-5" />
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          <FieldIcon className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{field.label}</p>
                            {field.required && (
                              <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Requerido</span>
                            )}
                            {field.show_in_reports && (
                              <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">En informes</span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">
                            <code className="bg-slate-100 px-1 rounded text-xs">{field.name}</code>
                            <span className="mx-2">•</span>
                            {fieldTypes.find(ft => ft.value === field.type)?.label || field.type}
                          </p>
                          {field.description && (
                            <p className="text-xs text-slate-400 mt-1">{field.description}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(field)}
                          className="text-slate-500 hover:text-slate-900"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteField(field.id)}
                          className="text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope']">
                {editingField ? "Editar Campo" : "Nuevo Campo"}
              </DialogTitle>
              <DialogDescription>
                Configura las propiedades del campo personalizado
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre interno *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                    placeholder="nombre_campo"
                  />
                  <p className="text-xs text-slate-400">Sin espacios, solo minúsculas</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="label">Etiqueta *</Label>
                  <Input
                    id="label"
                    value={formData.label}
                    onChange={(e) => setFormData({...formData, label: e.target.value})}
                    placeholder="Nombre del Campo"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="type">Tipo de campo</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldTypes.map(ft => (
                      <SelectItem key={ft.value} value={ft.value}>
                        <div className="flex items-center gap-2">
                          <ft.icon className="w-4 h-4" />
                          {ft.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.type === "select" && (
                <div className="space-y-2">
                  <Label htmlFor="options">Opciones (separadas por coma)</Label>
                  <Input
                    id="options"
                    value={formData.options}
                    onChange={(e) => setFormData({...formData, options: e.target.value})}
                    placeholder="Opción 1, Opción 2, Opción 3"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe el propósito de este campo..."
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="default_value">Valor por defecto</Label>
                <Input
                  id="default_value"
                  value={formData.default_value}
                  onChange={(e) => setFormData({...formData, default_value: e.target.value})}
                  placeholder="Valor inicial (opcional)"
                />
              </div>
              
              <div className="flex items-center justify-between border-t border-slate-200 pt-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="required"
                    checked={formData.required}
                    onCheckedChange={(checked) => setFormData({...formData, required: checked})}
                  />
                  <Label htmlFor="required" className="cursor-pointer">Campo requerido</Label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    id="show_in_reports"
                    checked={formData.show_in_reports}
                    onCheckedChange={(checked) => setFormData({...formData, show_in_reports: checked})}
                  />
                  <Label htmlFor="show_in_reports" className="cursor-pointer">Mostrar en informes</Label>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
